import React, { createContext, useState } from 'react';
import { getContextFactory } from '@ax2/common/contexts/ContextFactory';
import { DropdownOption } from '@ax2/common/components/Dropdown';
import PanelType from '@ax2/common/model/PanelType';

export interface PanelRecommendationAnswers {
  q1: number;
  q2: number;
}

export type PanelQuestion = keyof PanelRecommendationAnswers;

/**
 * Shorter versions are given so that recommendations logic is more readable
 * @see getRecommendations
 */

const Q1_OPTIONS = [
  'Reaction to value proposition or product messaging',
  'Market opportunity analysis',
  'Formulary receptivity and potential restrictions',
  'Customer relationship assessment',
  'Change to competitive set – new data, new indication, new product, loss of exclusivity, biosimilar',
  'Market trends',
  'Policy changes',
];

const Q2_OPTIONS = [
  'Administered/taken by patient (and not an oncology product)',
  'Administered by healthcare professional (and not an oncology product)',
  'Oncology product',
  'Not sure',
];

export function getDropdownOptions(
  step: PanelQuestion
): DropdownOption<number>[] {
  const opts = step === 'q1' ? Q1_OPTIONS : Q2_OPTIONS;
  return opts.map((opt, idx) => ({
    title: opt,
    value: idx + 1,
  }));
}

export interface RecommendedPanel {
  type: PanelType;
  rationale: JSX.Element;
}

const healthPlanAndPbmSummary = (
  <p>
    A health plan and PBM survey reaches key health plan and PBM decision
    makers, director-level and above, that sit on Pharmacy and Therapeutics
    (P&T) committees and make decisions about pharmaceutical products—how they
    are covered, how easy they are for patients to get, and what type of
    contracting opportunities exist. This survey captures both the health plan
    and the PBM perspective.
  </p>
);

const oncologyCenterSummary = (
  <p>
    An oncology survey reaches key decision makers from top oncology centers.
    The oncology center panel is focused on the top 200 oncology centers in the
    US and includes stand-alone oncology groups, university affiliated cancer
    clinics and health system affiliated cancer centers.
  </p>
);

const healthPlanSummary = (
  <p>
    A health plan survey reaches key health plan decision makers, director-level
    and above, that sit on Pharmacy and Therapeutics (P&T) committees and make
    access decisions about pharmaceutical products—how they are covered, how
    easy they are for patients to get, and what type of contracting
    opportunities exist. This survey captures only the health plan perspective.
  </p>
);

const hospitalNetworkSummary = (
  <p>
    A hospital system survey reaches key decision makers at large health systems
    and hospitals, director-level and above, that sit on Pharmacy and
    Therapeutics (P&T) committees and make access decisions about pharmaceutical
    products—how they are covered, how easy they are for patients to get, and
    what type of contracting opportunities exist. This survey captures the
    perspective of health system and hospital inpatient formularies.
  </p>
);

const healthPlanAndHospitalNetworkSummary = (
  <p>
    A health plan and hospital system survey reaches key decision makers at
    health plans and large health systems and hospitals, director-level and
    above, that sit on Pharmacy and Therapeutics (P&T) committees and make
    decisions about pharmaceutical products—how they are covered, how easy they
    are for patients to get, and what type of contracting opportunities exist.
    This survey captures both the health plan and hospital system perspective.
  </p>
);

const physicianSurveySummary = (
  <p>
    Physician Panel Summary: A physician survey reaches a global panel of
    physicians who treat patients in various practice settings across 25+
    specialties.
  </p>
);

export function getSummaryByPanel(panel: PanelType) {
  switch (panel) {
    case PanelType.HealthPlan:
      return healthPlanSummary;
    case PanelType.HealthPlanAndHospitalNetwork:
      return healthPlanAndHospitalNetworkSummary;
    case PanelType.HealthPlanAndPbm:
      return healthPlanAndPbmSummary;
    case PanelType.HospitalNetwork:
      return hospitalNetworkSummary;
    case PanelType.OncologyCenter:
      return oncologyCenterSummary;
    case PanelType.PhysicianSurvey:
      return physicianSurveySummary;
  }
}

export function getRecommendations({
  q1,
  q2,
}: PanelRecommendationAnswers): RecommendedPanel[] {
  if (q1 === 1 && q2 === 1) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For a self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For a self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 1 && q2 === 2) {
    return [
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For HCP-administered product</li>
            <li>
              Need both payer/health plan and hospital system perspectives
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 1 && q2 === 3) {
    return [
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For an oncology product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For HCP-administered oncology product</li>
            <li>
              Need both payer/health plan and hospital system perspectives
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For a self-administered oncology product</li>
            <li>Need both payer/health plan and PBM perspectives</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 1 && q2 === 4) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For a self-administered oncology product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For a self-administered oncology product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Need reaction to value proposition or product messaging</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 2 && q2 === 1) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For a self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For a self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 2 && q2 === 2) {
    return [
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For HCP-administered product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 2 && q2 === 3) {
    return [
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For an oncology product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For HCP-administered oncology product</li>
            <li>
              Need both payer/health plan and hospital system perspectives
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For self-administered oncology product</li>
            <li>Need both payer/health plan and PBM perspectives</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 2 && q2 === 4) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Seek to assess a market opportunity</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 3 && q2 === 1) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 3 && q2 === 2) {
    return [
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For HCP-administered product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 3 && q2 === 3) {
    return [
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For an oncology product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For HCP-administered oncology product</li>
            <li>
              Need both payer/health plan and hospital system perspectives
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For self-administered oncology product</li>
            <li>Need both payer/health plan and PBM perspectives</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 3 && q2 === 4) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Assess formulary receptivity, access restrictions</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 5 && q2 === 1) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 5 && q2 === 2) {
    return [
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For HCP-administered product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 5 && q2 === 3) {
    return [
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For an oncology product</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For HCP-administered oncology product</li>
            <li>
              Need both payer/health plan and hospital system perspectives
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For self-administered oncology product</li>
            <li>Need both payer/health plan and PBM perspectives</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 5 && q2 === 4) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For self-administered product</li>
            <li>
              Anticipate wide distribution network (or unsure about distribution
              network)
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlanAndHospitalNetwork,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For self-administered product</li>
            <li>Anticipate limited distribution network</li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For HCP-administered product</li>
            <li>Only need payer/health plan perspective</li>
            <li>Do not need hospital system perspective</li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>Assess impact of a change to the competitive market set</li>
            <li>For HCP-administered product</li>
            <li>Only need hospital system perspective</li>
            <li>Do not need payer/health plan perspective</li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 4) {
    return [
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>
              Assess quality of relationship between health plans and
              biopharmaceutical/biotech company
            </li>
            <li>
              Assess performance of biopharmaceutical company account managers
              or performance of competitors’ account managers
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>
              Assess quality of relationship between hospital systems and
              biopharmaceutical/biotech company
            </li>
            <li>
              Assess performance of biopharmaceutical company sales
              representatives or performance of competitors’ sales
              representatives
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>
              Assess quality of relationship between oncology centers and
              biopharmaceutical/biotech company
            </li>
            <li>
              Assess performance of biopharmaceutical company sales
              representatives or performance of competitors’ sales
              representatives
            </li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 6) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>
              Assess how a market trend may impact or be impacted by health
              plans and PBMs
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>
              Assess how a market trend may impact or be impacted by health
              plans
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>
              Assess how a market trend may impact or be impacted by hospital
              systems
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>
              Assess how a market trend may impact or be impacted by top
              oncology centers in the US
            </li>
          </ul>
        ),
      },
    ];
  } else if (q1 === 7) {
    return [
      {
        type: PanelType.HealthPlanAndPbm,
        rationale: (
          <ul>
            <li>
              Assess how a policy change may impact the decisions and behavior
              of health plans and PBMs
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HealthPlan,
        rationale: (
          <ul>
            <li>
              Assess how a policy change may impact the decisions and behavior
              of health plans
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.HospitalNetwork,
        rationale: (
          <ul>
            <li>
              Assess how a policy change may impact the decisions and behavior
              of hospital systems
            </li>
          </ul>
        ),
      },
      {
        type: PanelType.OncologyCenter,
        rationale: (
          <ul>
            <li>
              Assess how a policy change may impact the decisions and behavior
              of top oncology centers in the US
            </li>
          </ul>
        ),
      },
    ];
  }
  return [];
}

const PanelRecommendationContext = createContext<PanelRecommendationAnswers | null>(
  null
);
const PanelRecommendationUpdateContext = createContext<
  (answers: PanelRecommendationAnswers | null) => void
>(() => undefined);

export const usePanelRecommendationContext = getContextFactory(
  PanelRecommendationContext
);
export const usePanelRecommendationUpdateContext = getContextFactory(
  PanelRecommendationUpdateContext
);

const PanelRecommendationContextProvider: React.FC = props => {
  const [answers, setAnswers] = useState<PanelRecommendationAnswers | null>(
    null
  );
  return (
    <PanelRecommendationContext.Provider value={answers}>
      <PanelRecommendationUpdateContext.Provider value={setAnswers}>
        {props.children}
      </PanelRecommendationUpdateContext.Provider>
    </PanelRecommendationContext.Provider>
  );
};

export default PanelRecommendationContextProvider;
